import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Box, Alert, useTheme } from '@mui/material'
import { read, utils } from 'xlsx'
import FilePicker from '../../../FilePicker'
import NoticeBanner from '../../../NoticeBanner'
import AutoCompleteInput from '../../../AutoCompleteInput'
import { ReactComponent as DocumentIcon } from '../../../../assets/Icons_Document.svg'

const DATA_START_INDEX = 7 // row 8 in Excel (0-based index 7)
const MAX_FILE_SIZE_IN_BYTES = 10000000 // 10 MB

export default function UploadStep({ onConfirm }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [fileName, setFileName] = useState(null)
  const [parsedRows, setParsedRows] = useState(null)
  const [fileError, setFileError] = useState(null)
  const [constructor, setConstructor] = useState(null)

  const handleFiles = (files) => {
    if (!Array.isArray(files) || files.length === 0) return
    const file = files[0]
    setFileError(null)
    setParsedRows(null)

    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const wb = read(ev.target.result, { type: 'array' })
        const sheetName =
          wb.SheetNames.find((n) => n === 'Demandes de Contrôle') ||
          wb.SheetNames[0]
        const ws = wb.Sheets[sheetName]
        const allRows = utils.sheet_to_json(ws, {
          header: 1,
          raw: false,
          defval: '',
        })

        const dataRows = allRows
          .slice(DATA_START_INDEX)
          .filter((row) => row.some((cell) => cell !== '' && cell != null))

        if (dataRows.length === 0) {
          setFileError(t('calendar-import.upload-empty-file'))
          setFileName(null)
          return
        }

        const mapped = dataRows.map((row) => ({
          nParc: (row[0] || '').toString().trim(),
          locationName: (row[1] || '').toString().trim(),
          date: (row[2] || '').toString().trim(),
          observations: (row[3] || '').toString().trim(),
        }))

        setFileName(file.name)
        setParsedRows(mapped)
      } catch (err) {
        setFileError(t('calendar-import.upload-parse-error'))
        setFileName(null)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleRemove = () => {
    setFileName(null)
    setParsedRows(null)
    setFileError(null)
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <AutoCompleteInput
        resource="constructors/autocomplete"
        label={t('calendar-import.upload-constructor')}
        value={constructor}
        onChange={setConstructor}
        canDelete
      />

      {fileName ? (
        <NoticeBanner
          message={fileName}
          color={theme.palette.primary.main}
          backgroundColor="#E8F3FA"
          IconElement={DocumentIcon}
          onButtonClick={handleRemove}
        />
      ) : (
        <FilePicker
          filesLimit={1}
          maxFileSize={MAX_FILE_SIZE_IN_BYTES}
          acceptedFiles={[
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          ]}
          onChange={handleFiles}
        />
      )}

      {fileError && <Alert severity="error">{fileError}</Alert>}

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          disabled={!parsedRows}
          onClick={() => onConfirm({ constructor, rows: parsedRows })}
        >
          {t('calendar-import.upload-next')}
        </Button>
      </Box>
    </Box>
  )
}
